import { DEFAULT_MANUAL_OFFER_STATUS } from './manualOperations.config'
import { type ManualOfferComponentType, type ManualOfferFormValues, type ManualOfferPayload } from './manualOperations.types'

const trimToNull = (value: string | undefined): string | null => {
    const trimmed = value?.trim() ?? ''
    return trimmed === '' ? null : trimmed
}

const toNumberOrNull = (value: string | undefined): number | null => {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : null
}

const toDubaiIsoDate = (dateLocal: string): string => new Date(`${dateLocal}T12:00:00+04:00`).toISOString()

export const getManualOfferComponentType = (listingType: string | null, clientType: string): ManualOfferComponentType | null => {
    if (listingType === 'Rent' && clientType === 'Individual') return 'residential'
    if (listingType === 'Rent' && clientType === 'Company') return 'commercial'
    if (listingType === 'Sale' && clientType === 'Individual') return 'sale'
    if (listingType === 'Sale' && clientType === 'Company') return 'sale-commercial'
    return null
}

export const buildManualOfferPayload = (values: ManualOfferFormValues, documentIds: number[]): ManualOfferPayload => {
    const componentType = getManualOfferComponentType(values.unit.listingType, values.clientType)
    if (componentType == null) {
        throw new Error('Unsupported unit listing type.')
    }

    const processStartDate = toDubaiIsoDate(values.processStartDate)
    const processEndDate = toDubaiIsoDate(values.processEndDate)
    const offerType: Record<string, any> = {
        __component: `offer.${componentType}`,
        Offer: Number(values.amount),
        FirstName: trimToNull(values.firstName),
        LastName: trimToNull(values.lastName),
        Country: trimToNull(values.country),
        Email: trimToNull(values.email),
        Phone: trimToNull(values.phone),
        AdditionalNotes: trimToNull(values.notes)
    }

    if (values.clientType === 'Company') {
        offerType.BusinessName = trimToNull(values.businessName)
        offerType.BusinessOwnerName = trimToNull(values.businessOwnerName)
        offerType.BusinessActivity = trimToNull(values.businessActivity)
        offerType.EstablishmentDate = trimToNull(values.establishmentDate)
        offerType.UAEBank = trimToNull(values.uaeBank)
    } else {
        offerType.Occupation = trimToNull(values.occupation)
        offerType.Employer = trimToNull(values.employer)
        offerType.Occupants = trimToNull(values.occupants)
        offerType.Smoking = values.smoking === 'Yes'
        offerType.Pets = values.pets === 'Yes'
        offerType.PetType = values.pets === 'Yes' ? trimToNull(values.petType) : null
        offerType.InUAESince = toNumberOrNull(values.inUAESince)
        offerType.UAEBank = trimToNull(values.uaeBank)
    }

    if (values.unit.listingType === 'Rent') {
        offerType.Payments = toNumberOrNull(values.payments)
        offerType.ContractStartDate = values.contractStartDate != null && values.contractStartDate.trim() !== ''
            ? toDubaiIsoDate(values.contractStartDate)
            : null
        offerType.Duration = toNumberOrNull(values.duration)
        offerType.DepositSlipLink = trimToNull(values.depositSlipLink ?? undefined)
    }

    if (values.unit.listingType === 'Sale') {
        offerType.Type = values.buyerType ?? null
        offerType.BankName = trimToNull(values.bankName)
        offerType.BankPreApproval = values.bankPreApproval === 'Yes'
        offerType.PreApprovedAmount = trimToNull(values.preApprovedAmount)
        offerType.PurchaseReason = values.purchaseReason ?? null
    }

    return {
        Name: `Offer - ${values.unit.propertyName} - ${values.unit.headline}`,
        Notify: 'Agent',
        Statuses: [DEFAULT_MANUAL_OFFER_STATUS],
        ProcessStartDate: processStartDate,
        ProcessEndDate: processEndDate,
        User: { id: values.userId },
        Unit: { id: values.unit.unitId },
        Type: [offerType],
        Documents: documentIds
    }
}
